/**
 * The appliance's real category for headers and work-order lists.
 *
 * Sources: the appliance's company category (`appliances.category`) and the
 * scanned type (`appliances.product_type`). A washer dryer named by either
 * one wins, because older scans saved washer dryers with category "Dryer"
 * and product_type "Washer Dryer". Otherwise the company category is used,
 * then the product type. Returns null only when the appliance has no
 * category at all, so callers can show "Other".
 */
export const APPLIANCE_CATEGORY = {
  WASHING_MACHINE: "Washing Machine",
  WASHER_DRYER: "Washer Dryer",
  TUMBLE_DRYER: "Tumble Dryer",
  DISHWASHER: "Dishwasher",
  REFRIGERATOR: "Refrigerator",
  OVEN: "Oven",
  VACUUM_CLEANER: "Vacuum Cleaner",
} as const;

const ALIASES: Record<string, string> = {
  washingmachine: APPLIANCE_CATEGORY.WASHING_MACHINE,
  washer: APPLIANCE_CATEGORY.WASHING_MACHINE,
  washerdryer: APPLIANCE_CATEGORY.WASHER_DRYER,
  washerdrier: APPLIANCE_CATEGORY.WASHER_DRYER,
  washingmachinedryer: APPLIANCE_CATEGORY.WASHER_DRYER,
  dryer: APPLIANCE_CATEGORY.TUMBLE_DRYER,
  tumbledryer: APPLIANCE_CATEGORY.TUMBLE_DRYER,
  dishwasher: APPLIANCE_CATEGORY.DISHWASHER,
  refrigerator: APPLIANCE_CATEGORY.REFRIGERATOR,
  householdrefrigerator: APPLIANCE_CATEGORY.REFRIGERATOR,
  fridge: APPLIANCE_CATEGORY.REFRIGERATOR,
  oven: APPLIANCE_CATEGORY.OVEN,
  vacuumcleaner: APPLIANCE_CATEGORY.VACUUM_CLEANER,
};

/** Values that mean "no category" rather than a real one. */
const EMPTY_KEYS = new Set(["", "other", "appliance", "unknown"]);

const keyOf = (raw: string | null | undefined) =>
  String(raw ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");

/** Known category label for a raw value, or null when it isn't a known category. */
export function knownApplianceCategory(raw: string | null | undefined): string | null {
  return ALIASES[keyOf(raw)] ?? null;
}

export function resolveApplianceCategory(input: {
  category?: string | null;
  productType?: string | null;
}): string | null {
  const fromCategory = knownApplianceCategory(input.category);
  const fromType = knownApplianceCategory(input.productType);
  if (fromCategory === APPLIANCE_CATEGORY.WASHER_DRYER || fromType === APPLIANCE_CATEGORY.WASHER_DRYER) {
    return APPLIANCE_CATEGORY.WASHER_DRYER;
  }
  if (fromCategory) return fromCategory;
  const custom = String(input.category ?? "").trim();
  if (!EMPTY_KEYS.has(keyOf(custom))) return custom;
  return fromType;
}

/** Header text "BRAND · CATEGORY" parts; `otherLabel` is used only when there is no category. */
export function applianceCategoryLabel(
  input: { category?: string | null; productType?: string | null },
  otherLabel = "Other",
): string {
  return resolveApplianceCategory(input) ?? otherLabel;
}
